import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';

import { Menu, X, User, Heart, Search, HelpCircle, Home, LogOut, MessageCircle, AlertTriangle, Flag } from 'lucide-react';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { user, logout, isLoggingOut } = useAuth();
  const location = useLocation();

  // Items de navegación base
  const baseNavItems = [
    { name: 'Inicio', path: '/', icon: Home },
    { name: user ? user.username : 'Mi Cuenta', path: '/mi-cuenta', icon: User },
    { name: 'Buscar Pareja', path: '/buscar-pareja', icon: Search },
    { name: 'Mensajes', path: '/mensajes', icon: MessageCircle },
    { name: 'Ayuda', path: '/ayuda', icon: HelpCircle },
  ];

  // Agregar "Reportes" si el usuario es admin
  const navItems = config.isAdmin(user)
    ? [...baseNavItems.slice(0, 4), { name: 'Reportes', path: '/reportes', icon: Flag }, ...baseNavItems.slice(4)]
    : baseNavItems;

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      setShowLogoutModal(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 hover:scale-105 active:scale-95 transition-all duration-200 group">
              <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-primary transition-transform duration-200 group-hover:rotate-12" />
              <span className="text-lg sm:text-xl font-bold text-text">SolterosMexico</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isActive(item.path)
                        ? 'text-primary bg-red-50 shadow-sm scale-105'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50 hover:scale-105'
                    }`}
                  >
                    <Icon className={`h-4 w-4 transition-transform duration-200 ${
                      isActive(item.path) ? 'scale-110' : 'group-hover:scale-110'
                    }`} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              
              {user && (
                <button
                  onClick={handleLogoutClick}
                  disabled={isLoggingOut}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ml-2 whitespace-nowrap ${
                    isLoggingOut 
                      ? 'text-gray-400 cursor-not-allowed opacity-75' 
                      : 'text-gray-600 hover:text-primary hover:bg-gray-50 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isLoggingOut ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      <span>Cerrando...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                      <span>Cerrar Sesión</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-primary hover:bg-gray-50 hover:scale-110 active:scale-95 transition-all duration-200"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col space-y-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 whitespace-nowrap ${
                        isActive(item.path)
                          ? 'text-primary bg-red-50'
                          : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
                
                {user && (
                  <button
                    onClick={handleLogoutClick}
                    disabled={isLoggingOut}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      isLoggingOut 
                        ? 'text-gray-400 cursor-not-allowed opacity-75' 
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50 hover:scale-105 active:scale-95'
                    }`}
                  >
                    {isLoggingOut ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        <span>Cerrando...</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 transition-transform duration-200 group-hover:rotate-12" />
                        <span>Cerrar Sesión</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Modal de confirmación de logout */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            {/* Header del modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar salida</h3>
              </div>
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres cerrar sesión? 
                Tendrás que volver a iniciar sesión para acceder a tu cuenta.
              </p>
              
              {/* Botones de acción */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2 ${
                    isLoggingOut 
                      ? 'bg-red-400 text-white cursor-not-allowed' 
                      : 'bg-red-600 text-white hover:bg-red-700 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isLoggingOut ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Cerrando...</span>
                    </>
                  ) : (
                    <span>Sí, salir</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
